import { useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/site/Header";
import Hero from "@/components/site/Hero";
import Marquee from "@/components/site/Marquee";
import Services from "@/components/site/Services";
import Gallery from "@/components/site/Gallery";
import About from "@/components/site/About";
import Process from "@/components/site/Process";
import Reviews from "@/components/site/Reviews";
import QuoteForm from "@/components/site/QuoteForm";
import Footer from "@/components/site/Footer";
import StickyPhoneBar from "@/components/site/StickyPhoneBar";
import ChatWidget from "@/components/site/ChatWidget";
import Admin from "@/pages/Admin";
import { useRef } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Landing = () => (
  <div className="min-h-screen bg-[#0B0C0E] text-[#F3F4F6] font-jakarta antialiased selection:bg-[#D97736] selection:text-[#0B0C0E]">
    <div className="grain-overlay" aria-hidden="true" />
    <Header />
    <main>
      <Hero />
      <Marquee />
      <Services />
      <Gallery />
      <About />
      <Process />
      <Reviews />
      <QuoteForm />
    </main>
    <Footer />
    <StickyPhoneBar />
    <ChatWidget />
  </div>
);

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    axios
      .post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true })
      .then((r) => navigate("/admin", { state: { user: r.data }, replace: true }))
      .catch((e) => {
        const detail = e.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : "Accesso con Google non riuscito. Riprova.";
        navigate("/admin", { state: { authError: msg }, replace: true });
      });
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0C0E] flex items-center justify-center">
      <Loader2 size={32} className="animate-spin text-[#D97736]" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/admin" element={<Admin />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="top-center" theme="dark" richColors />
    </BrowserRouter>
  );
}

export default App;
